import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Textarea,
} from '@nextui-org/react';
import { CtyButton, CtyShowToast, SimpleDeleteConfirmationModal, checkType, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import {
  type Credential,
  type DataConnector,
  useCreateDataConnectorMutation,
  useDeleteDataConnectorMutation,
  useGetMcpServersQuery,
  useToggleMcpServerMutation,
  useUpdateDataConnectorMutation,
} from '@circuitry-ai/doc-data';

const MCP_CONNECTOR_TYPE = 'MCP';

function readMcpServerIdFromMetadata(metadata?: Record<string, unknown>): string {
  const id = metadata?.mcp_server_id;
  return typeof id === 'string' ? id : '';
}

/** Canonical `connector_type` values sent to the API; labels are built with `t(key)` like the status field. */
export const CONNECTOR_TYPE_PRESETS: { key: string; label: string }[] = [
  { key: 'PostgreSQL', label: 'PostgreSQL' },
  { key: 'REST API', label: 'REST API' },
  { key: 'MySQL', label: 'MySQL' },
  { key: 'sharepoint', label: 'SharePoint' },
  { key: 'sitecore', label: 'SiteCore' },
  { key: 'MCP', label: 'MCP' },
] as const;

/** Match `Input` / `Textarea` `variant="bordered"` (no gray fill like `faded`). */
const CONNECTOR_SELECT_PROPS = {
  variant: 'bordered' as const,
  classNames: {
    trigger:
      'bg-transparent shadow-none data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent',
    value: 'text-default-500 group-data-[has-value=true]:!text-foreground',
  },
} as const;

export interface ConnectorFormProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** `null` = create; otherwise edit */
  readonly editingConnector: DataConnector | null;
  readonly credentialsList: Credential[];
  readonly credentialsLoading: boolean;
  readonly onSaved: () => void;
}

export function ConnectorForm({
  isOpen,
  onClose,
  editingConnector,
  credentialsList,
  credentialsLoading,
  onSaved,
}: ConnectorFormProps) {
  const { t } = useTranslation();
  const hasIntegrationsAccess = useHasApplicationResource('integrations');
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<string>('');
  const [formDescription, setFormDescription] = useState('');
  const [formCredentialId, setFormCredentialId] = useState<string>('');
  const [formMcpServerId, setFormMcpServerId] = useState<string>('');
  const [formStatus, setFormStatus] = useState<string>('Active');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isMcpConnector = formType === MCP_CONNECTOR_TYPE;
  const {
    data: mcpServers = [],
    isLoading: mcpServersLoading,
    isError: mcpServersError,
  } = useGetMcpServersQuery(undefined, { skip: !isOpen || !isMcpConnector });

  const [createConnector, { isLoading: isCreating }] = useCreateDataConnectorMutation();
  const [updateConnector, { isLoading: isUpdating }] = useUpdateDataConnectorMutation();
  const [deleteConnector, { isLoading: isDeleting }] = useDeleteDataConnectorMutation();
  const [toggleMcpServer] = useToggleMcpServerMutation();

  const editingId = editingConnector?.connector_id ?? null;

  const formTypeSelectItems = useMemo(() => {
    const base = CONNECTOR_TYPE_PRESETS.map((p: { key: string; label: string }) => ({
      key: p.key,
      label: p.label ?? p.key,
    }));
    const extra = editingConnector?.connector_type?.trim();
    const withExtra =
      extra && !base.some((i) => i.key === extra) ? [...base, { key: extra, label: t(extra) }] : base;
    return [...withExtra].sort((a, b) => a.label.localeCompare(b.label));
  }, [editingConnector?.connector_type, t]);

  const formStatusItems = useMemo(
    () => [
      { key: 'Active', label: t('Active') },
      { key: 'Inactive', label: t('In Active') },
    ],
    [t]
  );

  const mcpServerSelectItems = useMemo(() => {
    const enabledServers = mcpServers.filter((server) => server.enabled !== false);
    const items = enabledServers.map((server) => ({
      key: server.id,
      label: server.name,
    }));
    if (
      formMcpServerId &&
      !items.some((item) => item.key === formMcpServerId) &&
      editingConnector
    ) {
      const metadata = editingConnector.request_metadata;
      const fallbackName =
        typeof metadata?.mcp_server_name === 'string'
          ? metadata.mcp_server_name
          : formMcpServerId;
      items.push({ key: formMcpServerId, label: fallbackName });
    }
    return [...items].sort((a, b) => a.label.localeCompare(b.label));
  }, [mcpServers, formMcpServerId, editingConnector]);

  const resetForm = () => {
    setFormName('');
    setFormType('');
    setFormDescription('');
    setFormCredentialId('');
    setFormMcpServerId('');
    setFormStatus('Active');
  };

  useEffect(() => {
    if (!isOpen) return;
    if (editingConnector) {
      setFormName(editingConnector.connector_name ?? '');
      setFormType(editingConnector.connector_type ?? '');
      setFormDescription(editingConnector.description ?? '');
      setFormCredentialId(editingConnector.credential_id ?? '');
      setFormMcpServerId(readMcpServerIdFromMetadata(editingConnector.request_metadata));
      const raw = (editingConnector.status ?? '').toLowerCase();
      setFormStatus(raw === 'active' ? 'Active' : 'Inactive');
    } else {
      resetForm();
    }
  }, [isOpen, editingId, editingConnector]);

  useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!formName.trim() || !formType || !formCredentialId) {
      CtyShowToast(checkType.warning, t('Please fill required fields'));
      return;
    }
    if (isMcpConnector && !formMcpServerId) {
      CtyShowToast(checkType.warning, t('Please select an MCP server'));
      return;
    }

    const selectedMcpServer = mcpServers.find((server) => server.id === formMcpServerId);
    const mcpServerName =
      selectedMcpServer?.name ??
      (typeof editingConnector?.request_metadata?.mcp_server_name === 'string'
        ? editingConnector.request_metadata.mcp_server_name
        : '');

    const basePayload = {
      connector_name: formName.trim(),
      connector_type: formType,
      credential_id: formCredentialId,
      ...(formDescription.trim() ? { description: formDescription.trim() } : {}),
      status: formStatus,
      ...(isMcpConnector
        ? {
            request_metadata: {
              mcp_server_id: formMcpServerId,
              mcp_server_name: mcpServerName,
            },
          }
        : {}),
    };
    try {
      if (editingId) {
        await updateConnector({
          connector_id: editingId,
          payload: basePayload,
        }).unwrap();
        CtyShowToast(checkType.success, t('Connector updated successfully'));
      } else {
        await createConnector(basePayload).unwrap();
        CtyShowToast(checkType.success, t('Connector created successfully'));
      }

      if (isMcpConnector && formMcpServerId) {
        try {
          await toggleMcpServer({
            mcp_server_id: formMcpServerId,
            active: formStatus === 'Active',
          }).unwrap();
        } catch (mcpError) {
          CtyShowToast(
            checkType.warning,
            (mcpError as Error)?.message ?? t('Connector saved but failed to update MCP server status'),
          );
        }
      }

      onSaved();
      onClose();
    } catch (e) {
      CtyShowToast(
        checkType.error,
        (e as Error)?.message ?? (editingId ? t('Failed to update connector') : t('Failed to create connector'))
      );
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      const response = await deleteConnector(editingId).unwrap();
      CtyShowToast(checkType.success, response?.message ?? t('Connector deleted successfully'));
      setShowDeleteConfirm(false);
      onSaved();
      onClose();
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error)?.message ?? t('Failed to delete connector'));
    }
  };

  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isDeleting;

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[100] bg-default-900/40 backdrop-blur-[1px]"
        aria-label={t('Close')}
        onClick={onClose}
      />
      <aside
        className="fixed top-0 right-0 z-[110] flex h-full w-full max-w-[480px] flex-col border-l border-default-200 bg-content1 shadow-2xl"
        aria-labelledby="connectors-sheet-title"
      >
        <div className="flex items-start justify-between gap-2 border-b border-default-200 px-4 py-4">
          <div className="min-w-0">
            <h2 id="connectors-sheet-title" className="text-xl font-semibold text-foreground">
              {editingId ? t('Edit Connector') : t('New Connector')}
            </h2>
            <p className="text-sm text-default-500 mt-1">
              {editingId
                ? t('Update connector details and linked credential.')
                : t('Connect a data source and link credentials from the vault.')}
            </p>
          </div>
          <CtyButton isIconOnly variant="light" radius="md" onPress={onClose} aria-label={t('Close')}>
            <Icon icon="mdi:close" width={22} />
          </CtyButton>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
          <Input
            label={t('Connector name')}
            placeholder={t('e.g. Customer Analytics DB')}
            value={formName}
            onValueChange={setFormName}
            variant="bordered"
            isRequired
          />
          <Select
            label={t('Type')}
            placeholder={t('Select type')}
            items={formTypeSelectItems}
            selectedKeys={formType ? new Set([formType]) : new Set()}
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string;
              setFormType(v ?? '');
              if (v !== MCP_CONNECTOR_TYPE) {
                setFormMcpServerId('');
              }
            }}
            {...CONNECTOR_SELECT_PROPS}
            isRequired
          >
            {(item) => (
              <SelectItem key={item.key} textValue={item.label}>
                {item.label}
              </SelectItem>
            )}
          </Select>
          {isMcpConnector ? (
            <Select
              label={t('MCP Server')}
              placeholder={
                mcpServersLoading
                  ? t('Loading MCP servers...')
                  : mcpServersError
                    ? t('Failed to load MCP servers')
                    : t('Select MCP server')
              }
              items={mcpServerSelectItems}
              selectedKeys={formMcpServerId ? new Set([formMcpServerId]) : new Set()}
              onSelectionChange={(keys) => {
                const v = Array.from(keys)[0] as string;
                setFormMcpServerId(v ?? '');
              }}
              {...CONNECTOR_SELECT_PROPS}
              isRequired
              isLoading={mcpServersLoading}
              isDisabled={mcpServersLoading || mcpServersError}
            >
              {(item) => (
                <SelectItem key={item.key} textValue={item.label}>
                  {item.label}
                </SelectItem>
              )}
            </Select>
          ) : null}
          <Select
            label={t('Status')}
            placeholder={t('Select status')}
            items={formStatusItems}
            selectedKeys={formStatus ? new Set([formStatus]) : new Set()}
            onSelectionChange={(keys) => {
              const v = Array.from(keys)[0] as string;
              setFormStatus(v ?? 'Active');
            }}
            {...CONNECTOR_SELECT_PROPS}
            isRequired
          >
            {(item) => (
              <SelectItem key={item.key} textValue={item.label}>
                {item.label}
              </SelectItem>
            )}
          </Select>
          <Textarea
            label={t('Description')}
            placeholder={t('What will this connector be used for?')}
            value={formDescription}
            onValueChange={setFormDescription}
            variant="bordered"
            minRows={3}
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">
              {t('Credential')}
              <span className="text-danger">*</span>
            </span>
            <Autocomplete
              aria-label={t('Credential')}
              placeholder={credentialsLoading ? t('Loading...') : t('Select Credentials')}
              size="sm"
              variant="bordered"
              selectedKey={formCredentialId || null}
              isLoading={credentialsLoading}
              onSelectionChange={(key) => setFormCredentialId(key == null ? '' : String(key))}
            >
              {credentialsList.map((credential) => (
                <AutocompleteItem key={credential.credential_id} textValue={credential.name ?? credential.credential_id}>
                  {credential.name ?? credential.credential_id}
                </AutocompleteItem>
              ))}
            </Autocomplete>
          </div>
          <div className="text-xs text-default-600 bg-primary/10 border border-primary rounded-lg p-3">
            {t('Credentials are managed in Integration Credentials and mapped to this connector by credential_id.')}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-default-200 px-4 py-4">
          {editingId ? (
            <CtyButton
              color="danger"
              variant="light"
              isLoading={isDeleting}
              isDisabled={!hasIntegrationsAccess || isSaving}
              onPress={() => setShowDeleteConfirm(true)}
            >
              {t('Delete')}
            </CtyButton>
          ) : null}
          <CtyButton variant="bordered" isDisabled={isBusy} onPress={onClose}>
            {t('Cancel')}
          </CtyButton>
          <CtyButton color="primary" isLoading={isSaving} isDisabled={!hasIntegrationsAccess || isDeleting} onPress={() => void handleSave()}>
            {editingId ? t('Update') : t('Save')}
          </CtyButton>
        </div>
        <SimpleDeleteConfirmationModal
          isOpen={showDeleteConfirm}
          onOpenChange={(open) => {
            if (!open) setShowDeleteConfirm(false);
          }}
          entityName={editingConnector?.connector_name ?? ''}
          onConfirm={() => void handleDelete()}
          isConfirmLoading={isDeleting}
          cancelLabel={t('Close')}
          confirmLabel={t('Delete Connector')}
        />
      </aside>
    </>
  );
}
